import { FocusScope } from 'react-aria';
import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import { colorCss } from 'lib/inquiryStyles';
import { StepCombinedBaseConfig, StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import LoadingDocument from 'verify/components/LoadingDocument';
import { MIN_HEIGHTS } from 'verify/styles/variables';

export const TIMEOUT_INTERVAL = 20 * 1000;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

const AnimationContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  margin-top: ${SPACINGS.base * 8};
`;

interface InquiryGovernmentIdPendingViewProps {
  assets: StepGovernmentIdConfig['assets']['pending-page'];
  localizations: StepGovernmentIdConfig['localizations']['pending-page'];
  textVerticalPosition: StepCombinedBaseConfig['pending-page-text-vertical-position'];
}

export default function InquiryGovernmentIdPendingView({
  assets,
  localizations,
  textVerticalPosition,
}: InquiryGovernmentIdPendingViewProps) {
  const { base, processingPage } = useStepGovernmentIdStyleContext();

  const header = (
    <Header>
      <ThemedTitle styles={processingPage?.titleStyle}>
        <Markdown>{localizations.title}</Markdown>
      </ThemedTitle>
      <ThemedParagraph styles={processingPage?.textStyle}>
        <Markdown>{localizations.description}</Markdown>
      </ThemedParagraph>
    </Header>
  );

  return (
    <FocusScope autoFocus>
      <Container>
        <CustomAssetWithFallback
          customImage={assets['header-pictograph']}
          alt='header pictograph'
        />

        {textVerticalPosition !== 'end' && header}

        <AnimationContainer>
          <CustomAssetWithFallback
            customImage={assets['loading-pictograph']}
            alt='loading pictograph'
          >
            <LoadingDocument
              icon='verification'
              $primaryColor={colorCss(base?.fill.color)}
              $strokeColor={colorCss(base?.stroke.color)}
            />
          </CustomAssetWithFallback>
        </AnimationContainer>

        {textVerticalPosition === 'end' && header}
      </Container>
    </FocusScope>
  );
}
